import { MasterLookupItemsType, AccountCategoryKey } from "../../../../../utils/enums";
import { formatDateForInput } from "../../../../../utils/dateUtils";

/**
 * Shared input-builders for UPDATE_EMPLOYEE_DETAILS so single-row dialogs
 * (EditScopeDialog / EditTerminationDateDialog) and the bulk dialogs stay in
 * lockstep. Each builder returns one element of the `input: [...]` array.
 */

export const resolveEmployeeEmail = (employee) =>
  employee?.work_email ||
  employee?.emails?.find((e) => e.type === "work")?.email ||
  employee?.emails?.[0]?.email ||
  "";

/**
 * Build the scope-change input for a single employee.
 * Mirrors EditScopeDialog.handleSave.
 */
export const buildScopeInput = (
  employee,
  { inScope, accountCategory, scopeReason, currentUserName }
) => {
  const email = resolveEmployeeEmail(employee);
  if (!employee?.id || !email) return null;

  const defaultOutOfScopeReason = `Marked out of scope by ${
    currentUserName?.trim() || "admin"
  }`;

  const input = {
    id: employee.id,
    email,
    in_scope: inScope,
    scope_reason: inScope
      ? null
      : (scopeReason?.trim() || defaultOutOfScopeReason),
  };

  if (inScope) {
    input.employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
    input.employee_key = AccountCategoryKey.ACTIVE_USER;
  } else {
    input.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
    input.employee_key = accountCategory;
  }

  return input;
};

export const FUTURE_TERMINATION_DATE_MESSAGE =
  "Future termination dates aren't allowed. Setting one early would revoke the employee's Regodit access before they can complete required policies and training.";

/**
 * Validate a termination date for a single employee.
 * Returns an error message string, or null when valid.
 */
export const validateTerminationDate = (employee, terminationDate) => {
  if (!terminationDate) return null;

  const today = formatDateForInput(new Date());
  if (terminationDate.split("T")[0] > today) {
    return FUTURE_TERMINATION_DATE_MESSAGE;
  }

  if (employee?.start_date) {
    const startDate = new Date(employee.start_date);
    const termDate = new Date(terminationDate);
    if (startDate > termDate) {
      return "Termination date cannot be before the employee's start date";
    }
  }

  return null;
};

/**
 * Build the termination-date input for a single employee.
 * Mirrors EditTerminationDateDialog.handleSave (scope side-effects included).
 * `currentTerminationDate` lets a clear operation flip the employee back in scope.
 */
export const buildTerminationInput = (
  employee,
  { terminationDate, currentTerminationDate, terminationReason }
) => {
  const email = resolveEmployeeEmail(employee);
  if (!employee?.id || !email) return null;

  const input = {
    id: employee.id,
    email,
    termination_date: terminationDate ? terminationDate.split("T")[0] : null,
  };

  if (terminationDate) {
    input.in_scope = false;
    input.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
    input.employee_key = AccountCategoryKey.OFFBOARDED_USER;
    // Optional reason — stored in scope_reason (shown in "Reason for Inactivity").
    // Only set when provided so we don't clear an existing reason.
    if (terminationReason && terminationReason.trim()) {
      input.scope_reason = terminationReason.trim();
    }
  } else if (currentTerminationDate) {
    input.in_scope = true;
    input.employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
    input.employee_key = AccountCategoryKey.ACTIVE_USER;
    input.scope_reason = null;
  }

  return input;
};
